/**
 * Skynet · sk-cart-sync (módulo 3)
 * Si el carrito cambia en otra pestaña (o en el checkout) y el cliente vuelve a esta, el cajón y el
 * contador de Horizon se ponen al día solos. Horizon ya cubre «Atrás» (bfcache); esto cubre el resto.
 * - Solo lee /cart.js (nunca escribe nada en el carrito).
 * - Toma la foto del carrito cuando el cliente se va (pestaña oculta o ventana sin foco) y la compara
 *   al volver. Al cargar la página no pide nada.
 * - Si hay un cambio de Horizon o de las tarjetas de oferta en curso, espera a que termine y, si algo
 *   cambia mientras lee, no pinta datos viejos: vuelve a leer.
 * - Avisa a Horizon con el evento estándar CartLinesUpdateEvent ('update': nunca abre el cajón).
 * - Si algo falla, no pinta nada y no rompe nada.
 */
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const MIN_GAP_MS = 3000;
const TIMEOUT_MS = 8000;
const WAIT_PENDING_MS = 10000;

async function fetchWithTimeout(url, options = {}) {
  const c = new AbortController();
  const t = setTimeout(() => c.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: c.signal });
  } finally {
    clearTimeout(t);
  }
}

async function readCart() {
  const res = await fetchWithTimeout(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

/** Huella del carrito: cambia si cambia cualquier línea, cantidad, precio, descuento o moneda. */
function signature(cart) {
  if (!cart || !Array.isArray(cart.items)) return null;
  return [
    cart.item_count,
    cart.total_price,
    cart.total_discount,
    cart.currency,
    ...cart.items.map((i) => `${i.key}:${i.quantity}:${i.final_line_price}`),
  ].join('|');
}

function linesOf(cart) {
  return new Map((cart?.items || []).map((i) => [i.key, i.quantity]));
}

if (!window.__skCartSync) {
  window.__skCartSync = true;

  let known = null; // huella de lo que la página enseña
  let knownLines = new Map();
  let left = false; // el cliente se fue (pestaña oculta o ventana sin foco) desde la última comprobación
  let busy = false;
  let last = 0;
  let timer = 0;
  let gen = 0; // sube con cada cambio ajeno (Horizon, tarjetas de oferta…)
  const pending = new Map(); // promesas de cambios ajenos aún en curso → hora en que llegaron
  let unloading = false; // la página se está yendo (enlace interno): no se toma foto
  let restoredAt = 0; // vuelta desde la caché («Atrás»): Horizon ya repinta
  let snapAt = 0; // cuándo se resolvió la última foto
  let backAt = 0; // cuándo volvió el cliente
  let leftAt = 0; // cuándo se fue
  let retries = 0; // reintentos en esta vuelta
  let events = null;
  let mine = null; // el último evento que lanzamos nosotros (para no escucharnos)

  const loadEvents = async () => {
    if (!events) events = await import('@shopify/events').catch(() => null);
    return events;
  };

  const snapshot = async () => {
    try {
      const cart = await readCart();
      known = signature(cart);
      knownLines = linesOf(cart);
    } catch (e) {
      known = null; // sin foto: al volver se pinta lo que haya, por si acaso
    }
    snapAt = Date.now();
  };

  const waitPending = async () => {
    const horizon = customElements.get('cart-items-component')?.pendingQuantityUpdates || [];
    const now = Date.now();
    pending.forEach((at, p) => now - at > WAIT_PENDING_MS && pending.delete(p)); // promesas que nunca acaban
    const all = [...pending.keys(), ...horizon];
    if (!all.length) return;
    await Promise.race([Promise.allSettled(all), new Promise((r) => setTimeout(r, WAIT_PENDING_MS))]);
  };

  const later = () => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = 0;
      sync(true);
    }, Math.max(0, MIN_GAP_MS - (Date.now() - last)));
  };

  async function sync(force = false) {
    if (document.visibilityState !== 'visible') return;
    if (!left && !force) return;
    if (Date.now() - restoredAt < 1000) return;
    if (!force) {
      backAt = Date.now();
      retries = 0;
    }
    if (busy || Date.now() - last < MIN_GAP_MS) return later();
    busy = true;
    last = Date.now();
    left = false;
    let deferred = null;
    try {
      await snap; // la foto de al irse tiene que estar hecha antes de comparar
      await waitPending();
      // Si la foto se resolvió después de volver (página congelada al irse), no vale para comparar.
      if (snapAt > backAt && snapAt - leftAt > 1000) known = null;
      const startGen = gen;
      const cart = await readCart();
      const sig = signature(cart);
      if (!sig || sig === known) return;

      const ev = await loadEvents();
      if (!ev?.CartLinesUpdateEvent?.createPromise) return;

      // Líneas que han cambiado (las que desaparecen van con cantidad 0). Si solo cambian precios o
      // descuentos, van las actuales. El evento exige al menos una línea.
      const now = linesOf(cart);
      const lines = [];
      now.forEach((q, key) => knownLines.get(key) !== q && lines.push({ id: key, quantity: q }));
      knownLines.forEach((q, key) => !now.has(key) && lines.push({ id: key, quantity: 0 }));
      if (!lines.length) now.forEach((q, key) => lines.push({ id: key, quantity: q }));
      if (!lines.length) {
        known = sig;
        return;
      }

      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      let sections;
      if (sectionIds.length) {
        try {
          const res = await fetchWithTimeout(
            `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`,
            { cache: 'no-store' }
          );
          if (res.ok) sections = await res.json();
        } catch (e) {
          sections = undefined; // Horizon vuelve a pedir la sección por su cuenta
        }
      }

      // Si alguien tocó el carrito mientras leíamos, lo leído ya es viejo: no se pinta y se repite.
      if (gen !== startGen || pending.size) {
        if (retries < 2) {
          retries += 1;
          later();
        }
        return;
      }

      const payload = {
        cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections, didError: false, source: 'sk-cart-sync', itemCount: cart.item_count },
      };
      deferred = ev.CartLinesUpdateEvent.createPromise();
      mine = new ev.CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise });
      document.dispatchEvent(mine);
      deferred.resolve(payload);
      known = sig;
      knownLines = now;
    } catch (e) {
      deferred?.reject?.(e);
    } finally {
      busy = false;
    }
  }

  let snap = null; // foto en curso
  const leave = () => {
    if (unloading) return;
    left = true;
    leftAt = Date.now();
    snap = snapshot();
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') leave();
    else sync();
  });

  // Ventanas una al lado de otra (escritorio): el foco se va sin ocultar la pestaña. Si el foco solo
  // pasa a un iframe de esta misma página (vídeo, pago), no cuenta como irse.
  window.addEventListener('blur', () => {
    setTimeout(() => {
      if (!document.hasFocus() && document.visibilityState === 'visible') leave();
    }, 0);
  });
  window.addEventListener('focus', () => sync());

  // «Atrás» desde la caché: Horizon ya repinta el carrito; aquí no se repite.
  window.addEventListener('pagehide', () => {
    unloading = true;
  });
  window.addEventListener('pageshow', (e) => {
    unloading = false;
    if (!e.persisted) return;
    clearTimeout(timer);
    timer = 0;
    left = false;
    known = null;
    last = Date.now();
    restoredAt = Date.now();
  });

  // Cambios de otros en esta página: se marcan para no pintar encima ni con datos viejos.
  loadEvents().then((ev) => {
    const name = ev?.StandardEvents?.cartLinesUpdate;
    if (!name) return;
    document.addEventListener(name, (e) => {
      if (e === mine) return;
      gen += 1;
      const p = Promise.resolve(e.promise)
        .catch(() => {})
        .finally(() => {
          pending.delete(p);
          gen += 1;
          // Lo que pintó el otro es lo que se ve ahora: la próxima foto se toma al irse.
          known = null;
        });
      pending.set(p, Date.now());
    });
  });
}
